import React, { useEffect, useState } from 'react';
import { Card, Form, Input, Button, message, Avatar, Divider, Row, Col, Statistic } from 'antd';
import { UserOutlined, EditOutlined, SaveOutlined, WalletOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../services/api';

const ProfilePage = () => {
  const navigate = useNavigate();
  const { user, updateProfile } = useAuth();
  const [form] = Form.useForm();
  const [passwordForm] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [changePasswordLoading, setChangePasswordLoading] = useState(false);

  useEffect(() => {
    if (user) {
      form.setFieldsValue({
        nickname: user.nickname || user.username,
        username: user.username,
        email: user.email,
      });
    }
  }, [user, form]);

  const handleSave = async (values) => {
    setLoading(true);
    try {
      const result = await updateProfile({ nickname: values.nickname });
      
      if (result.success) {
        message.success('个人信息更新成功');
        setEditing(false);
      } else {
        message.error(result.error || '更新失败');
      }
    } catch (error) {
      console.error('更新失败:', error);
      message.error('更新失败');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async (values) => {
    setChangePasswordLoading(true);
    try {
      const response = await api.post('/auth/change-password', {
        old_password: values.oldPassword,
        new_password: values.newPassword,
      });
      
      message.success('密码修改成功');
      // 重置密码表单
      passwordForm.resetFields();
    } catch (error) {
      console.error('修改密码失败:', error);
      message.error(error.response?.data?.error || '修改密码失败');
    } finally {
      setChangePasswordLoading(false);
    }
  };

  if (!user) {
    return (
      <div style={{ padding: 24, textAlign: 'center' }}>
        <p>请先登录</p>
        <Button type="primary" onClick={() => navigate('/login')}>
          去登录
        </Button>
      </div>
    );
  }

  return (
    <div style={{ padding: 24, maxWidth: 800, margin: '0 auto' }}>
      <h1 style={{ marginBottom: 24 }}>个人中心</h1>
      
      <Row gutter={[24, 24]}>
        {/* 左侧：用户信息卡片 */}
        <Col xs={24} md={8}>
          <Card style={{ textAlign: 'center' }}>
            <Avatar 
              size={100} 
              icon={<UserOutlined />} 
              src={user.avatar}
              style={{ marginBottom: 16 }}
            />
            <h2 style={{ margin: '0 0 8px 0' }}>
              {user.nickname || user.username}
            </h2>
            <p style={{ color: '#999', margin: 0 }}>@{user.username}</p>
            <p style={{ color: '#666', margin: '8px 0' }}>{user.email}</p>
            
            <Divider />
            
            <Statistic
              title="积分"
              value={user.points || 0}
              prefix={<WalletOutlined />}
            />
            
            <Divider />
            
            <Button 
              type="primary" 
              icon={<EditOutlined />}
              onClick={() => setEditing(!editing)}
              block
              style={{ marginBottom: 8 }}
            >
              {editing ? '取消编辑' : '编辑资料'}
            </Button>
            <Button 
              icon={<WalletOutlined />}
              onClick={() => navigate('/transactions')}
              block
              style={{ background: '#252532', borderColor: '#2a2a3a', color: '#e8e8ec' }}
            >
              查看交易记录
            </Button>
          </Card>
        </Col>
        
        {/* 右侧：编辑表单 */}
        <Col xs={24} md={16}>
          <Card title="基本信息">
            <Form
              form={form}
              layout="vertical"
              onFinish={handleSave}
              disabled={!editing}
            >
              <Form.Item
                name="nickname"
                label="昵称"
                rules={[
                  { required: true, message: '请输入昵称' },
                  { min: 2, message: '昵称至少2个字符' },
                  { max: 20, message: '昵称最多20个字符' },
                ]}
                extra="昵称是您在平台上显示的名称，其他用户将通过昵称识别您"
              >
                <Input placeholder="请输入昵称" />
              </Form.Item>
              
              <Form.Item
                name="username"
                label="用户名"
                extra="用户名不可修改，用于登录"
              >
                <Input disabled />
              </Form.Item>
              
              <Form.Item
                name="email"
                label="邮箱"
                extra="邮箱不可修改"
              >
                <Input disabled />
              </Form.Item>
              
              {editing && (
                <Form.Item>
                  <Button
                    type="primary"
                    htmlType="submit"
                    icon={<SaveOutlined />}
                    loading={loading}
                  >
                    保存修改
                  </Button>
                </Form.Item>
              )}
            </Form>
          </Card>
          
          <Card title="账号信息" style={{ marginTop: 24 }}>
            <p><strong>会员类型：</strong>{user.type || '普通会员'}</p>
            <p><strong>注册时间：</strong>{user.created_at ? new Date(user.created_at).toLocaleDateString() : '-'}</p>
            <p><strong>最后登录：</strong>{user.last_login ? new Date(user.last_login).toLocaleString() : '-'}</p>
          </Card>
          
          <Card title="修改密码" style={{ marginTop: 24 }}>
            <Form form={passwordForm} onFinish={handleChangePassword} layout="vertical">
              <Form.Item
                name="oldPassword"
                label="旧密码"
                rules={[{ required: true, message: '请输入旧密码' }]}
              >
                <Input.Password placeholder="请输入旧密码" />
              </Form.Item>
              
              <Form.Item
                name="newPassword"
                label="新密码"
                rules={[
                  { required: true, message: '请输入新密码' },
                  { min: 6, message: '新密码至少6个字符' }
                ]}
              >
                <Input.Password placeholder="请输入新密码" />
              </Form.Item>
              
              <Form.Item
                name="confirmPassword"
                label="确认密码"
                dependencies={['newPassword']}
                rules={[
                  { required: true, message: '请确认新密码' },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || getFieldValue('newPassword') === value) {
                        return Promise.resolve();
                      }
                      return Promise.reject(new Error('两次输入的密码不一致'));
                    },
                  }),
                ]}
              >
                <Input.Password placeholder="请确认新密码" />
              </Form.Item>
              
              <Form.Item>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={changePasswordLoading}
                >
                  修改密码
                </Button>
              </Form.Item>
            </Form>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default ProfilePage;
